import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChildren,
  ElementRef,
  input,
  OnDestroy,
  Optional,
  QueryList,
  Self,
  signal,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NgControl } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, fromEvent, Subject, throttleTime } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MuiOption } from '../option/mui-option.component';
import { isControlValid } from '../utils';
import { muiSelectErrorMultiModeTypeMismatch } from './mui-select-errors';


let nextUniqueId = 0;

@Component({
  selector: 'mui-select',
  templateUrl: './mui-select.component.html',
  styles: [
    `
      .mui-select__option-checked-icon {
        line-height: 1.5;
      }
    `
  ],
  host: {
    /* eslint-disable  @typescript-eslint/naming-convention */
    '[class]': 'classes()'
    /* eslint-enable  @typescript-eslint/naming-convention */
  },
  exportAs: 'muiSelect',
  encapsulation: ViewEncapsulation.None
})
export class MuiSelect<T> implements AfterContentInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  // Attributes/Labels signals
  id = input(`mui-select-${nextUniqueId++}`);
  label = input('');
  placeholder = input('');
  name = input<string | null>(null);
  inputId = computed(() => this.id() + '-input');
  labelId = computed(() => this.id() + '-label');

  isOpen = signal(false);
  isMobile = signal(false);
  circularNavigation = input(true);
  valueTemplate = input<TemplateRef<any> | undefined>(undefined);
  optionTemplate = input<TemplateRef<any> | undefined>(undefined);

  _closeOnSelect = input(true, {
    alias: 'closeOnSelect'
  });
  closeOnSelect = computed(() => this._closeOnSelect() && !this.multi());

  // Input received from the parent component to disable the select
  _inputDisabled = input(false, {
    transform: (value: boolean) => coerceBooleanProperty(value),
    alias: 'disabled'
  });
  // Used for control value accessor to disable the select
  formControlDisabled = signal(false);
  // Combines the previous two so that the select can be disabled from both the @input and the form control
  disabled = computed(() => this._inputDisabled() || this.formControlDisabled());

  required = input(false, {
    transform: (value: boolean) => coerceBooleanProperty(value)
  });

  multi = input(false, {
    transform: (value: boolean) => coerceBooleanProperty(value)
  });

  classes = computed(() => {
    const classes = ['mui-select'];

    if (this.disabled()) {
      classes.push('is--disabled');
    }

    if (this.isOpen()) {
      classes.push('is--open');
    }

    if (this.isMobile()) {
      classes.push('is--mobile');
    }

    return classes.join(' ');
  });

  get valid(): boolean {
    return this._control ? isControlValid(this._control) : true;
  }

  get optionsFocusable(): boolean {
    return this._optionsFocusable;
  }

  @ContentChildren(MuiOption) options!: QueryList<MuiOption<T>>;
  @ViewChildren('options') optionElements!: QueryList<ElementRef>;

  @ViewChild('select', { static: false }) nonNativeSelectField!: ElementRef<HTMLUListElement>;
  @ViewChild('optionList', { static: false }) optionList!: ElementRef<HTMLUListElement>;
  @ViewChild('overlayTemplate') _overlayTemplate!: TemplateRef<any>;

  model: MuiOption<T> | null = null;
  multiModel: Array<MuiOption<T>> = [];

  protected readonly document = document;
  protected mouseDown = false;

  private _overlayRef: OverlayRef | null = null;
  private _currentValue: T | T[] | null | undefined;
  private _optionsFocusable = false;
  private _destroy$ = new Subject<void>();

  constructor(
    private deviceDetectorService: DeviceDetectorService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    @Self() @Optional() protected _control: NgControl
  ) {
    if (_control) {
      _control.valueAccessor = this;
    }
    this.isMobile.set(this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet());
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngAfterContentInit(): void {
    this.options.changes.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.writeValue(this._currentValue);
    });

    // Make sure that the internal model is correctly set when the value is updated before the options array
    if (this.options.length) {
      this.writeValue(this._currentValue);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();

    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  writeValue(value: T | T[] | null | undefined): void {
    this._currentValue = value;

    if (value === null || value === undefined) {
      this.multiModel = [];
      this.model = null;
      return;
    }

    if (this.multi() && !Array.isArray(value)) {
      throw muiSelectErrorMultiModeTypeMismatch();
    }

    if (!this.options) {
      return;
    }

    if (this.multi()) {
      this.multiModel = this.options.filter((item) => (value as T[]).includes(item.value));
    } else {
      this.model = this.options.find((item) => item.value === value) ?? null;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDisabled.set(isDisabled);
  }

  /**
   * Select a provided MuiOption. Only triggers a change in the model when the option is not the currently selected one
   * and neither disabled.
   *
   * @param newOption The new option.
   */
  onChange(newOption: MuiOption<T>): void {
    if (this.disabled()) {
      return;
    }

    if (this.multi()) {
      if (this.isSelectedOption(newOption)) {
        // Remove item from model
        this.multiModel.splice(this.multiModel.indexOf(newOption), 1);
      } else {
        // Insert as first item in array
        this.multiModel.unshift(newOption);
      }

      this._currentValue = this.multiModel.map((opt) => opt.value);
    } else if (!this.isSelectedOption(newOption) && !newOption.disabled) {
      // Single mode, so lets set the single mode model
      this.model = newOption;
      this._currentValue = newOption.value;
    }

    this.propagateChange(this._currentValue);

    if (this.closeOnSelect()) {
      this.nonNativeSelectField.nativeElement.focus({ preventScroll: true });
      this.close();
    }
  }

  registerOnChange(fn: (_: T | T[] | null | undefined) => unknown): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => unknown): void {
    this.registerOnTouched = fn;
  }

  selectOption(option: MuiOption<T>): void {
    this.onChange(option);
  }

  isSelectedOption(option: MuiOption<T>): boolean {
    if (this.multi()) {
      return this.multiModel.includes(option);
    } else {
      return this.model === option;
    }
  }

  open(): void {
    if (this.mouseDown) {
      return;
    }

    if (this.isOpen() && this._overlayRef && this._overlayRef.hasAttached()) {
      return;
    }

    this.isOpen.set(true);
    this._optionsFocusable = false;
    this.propagateTouch();
    this.cdr.detectChanges();

    this.createAndAttachOverlay();
  }

  createAndAttachOverlay(): void {
    if (this._overlayRef && this._overlayRef.hasAttached()) {
      // If already attached, no need to create again
      return;
    }

    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom'
      }
    ];

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.nonNativeSelectField)
      .withPositions(positions)
      .withPush(true);

    this._overlayRef = this.overlay.create({
      positionStrategy: positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.close(),
      minWidth: this.nonNativeSelectField.nativeElement.offsetWidth,
      maxWidth: this.nonNativeSelectField.nativeElement.offsetWidth
    });

    const portal = new TemplatePortal(this._overlayTemplate, this.viewContainerRef);
    this._overlayRef.attach(portal);

    this._overlayRef.backdropClick().pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.close();
    });


    if (this._overlayRef.overlayElement) {
      fromEvent(document, 'scroll')
        .pipe(
          filter(() => this.isOpen()),
          throttleTime(50),
          takeUntil(this._destroy$)
        )
        .subscribe(() => {
          this.handleEscapeKey();
        });
    }
  }

  handleEscapeKey() {
    this.nonNativeSelectField.nativeElement.blur();
    this.close();
  }

  close(): void {
    if (this._overlayRef && this._overlayRef.hasAttached()) {
      this._overlayRef.detach();
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    this.isOpen.set(false);
    this.cdr.detectChanges();
  }

  toggleDropdown(): void {
    const shouldOpen = this.disabled() ? false : !this.isOpen();

    if (shouldOpen) {
      this.open();
      this.propagateTouch();
      this._optionsFocusable = false;
    } else {
      this.close();
    }
  }

  /**
   * Programmatically focus the option with a given index. If the option with the given index does not exist, nothing
   * will happen.
   *
   * @param i The index of the option to focus
   * @param dir The direction to focus in.
   */
  focusOption(i: number, dir?: 'next' | 'previous'): void {
    const options = this.optionElements;

    if (!(options?.length > 0)) {
      return;
    }

    this._optionsFocusable = true;
    const indexInBounds = i < options.length || i >= 0;

    // Make sure i is within bounds
    if (i < 0 && this.circularNavigation()) {
      // Go to the end of the list
      i = options.length - 1;
    } else if (i >= options.length && this.circularNavigation()) {
      // Go to the first item in the list
      i = 0;
    } else if (!this.circularNavigation() && !indexInBounds) {
      return;
    }

    const allElements: HTMLElement[] = options.map((ref) => ref.nativeElement);
    const element: HTMLElement = allElements[i];

    if (!element.hasAttribute('disabled')) {
      // Element is not disabled, set focus

      element.focus();
    } else if (dir && allElements.filter((e) => !e.hasAttribute('disabled')).length > 0) {
      // If at least one element is not disabled (preventing a loop here), try to focus the next option
      const newIndex = dir === 'next' ? i + 1 : i - 1;
      this.focusOption(newIndex, dir);
    }
  }

  /**
   * Focus the next element by providing the one that currently has focus. When no element is provided, the currently
   * active element from the document is taken. If that is not an option element of this select, nothing will happen.
   *
   * @param current The current element
   */
  focusNextOption(current?: Element): void {
    const currentElement = current ? current : document.activeElement;
    if (currentElement) {
      const i = this.getOptionIndex(currentElement);
      if (i >= 0) {
        this.focusOption(i + 1, 'next');
      }
    }
  }

  /**
   * Focus the previous element by providing the one that currently has focus. When no element is provided, the
   * currently active element from the document is taken. If that is not an option element of this select, nothing will
   * happen.
   *
   * @param current The current element
   */
  focusPreviousOption(current?: Element): void {
    const currentElement = current ? current : document.activeElement;
    if (currentElement) {
      const i = this.getOptionIndex(currentElement);
      if (i >= 0) {
        this.focusOption(i - 1, 'previous');
      }
    }
  }

  protected onOptionClick($event: MouseEvent, option: MuiOption<T>): void {
    if (!option.disabled) {
      this.selectOption(option);
    } else {
      $event.stopPropagation();
    }
  }

  protected nativeSelectOption(event: Event): void {
    // Find the native selected options and map them to the MuiOptions
    const target = event.target as HTMLSelectElement;
    const optionElements = Array.from(target.options);
    const selected: MuiOption<T>[] = optionElements.filter((option) => option.selected).map((option) => {
      if (this.placeholder()
        && target.options[0].disabled
        && target.options[0].value === 'undefined'
        && !this.multi()) {
        // select one element before from the list, because of the placeholder
        return this.options.toArray()[option.index - 1];
      }
      return this.options.toArray()[option.index];
    });

    // Update the model
    if (this.multi()) {
      this.model = null;
      this.multiModel = selected;
      this._currentValue = this.multiModel.map((opt) => opt.value);
    } else {
      this.multiModel = [];
      this.model = selected[0];
      this._currentValue = this.model.value;
    }

    // Update the form control
    this.propagateChange(this._currentValue);
  }

  protected getOptionId(i: number): string {
    return `${this.id()}-option-${i}`;
  }

  protected getActiveDescendantId(): string | null {
    if (!this.optionElements) {
      return null;
    }

    const activeOptionIndex = this.optionElements.toArray().map((e) => e.nativeElement).indexOf(document.activeElement);

    return activeOptionIndex >= 0 ? this.getOptionId(activeOptionIndex) : null;
  }

  protected onOptionArrowDown(event: KeyboardEvent, current?: Element): void {
    event.preventDefault(); // Prevent arrow key scrolling
    this.focusNextOption(current);
  }

  protected onOptionArrowUp(event: KeyboardEvent, current?: Element): void {
    event.preventDefault(); // Prevent arrow key scrolling
    this.focusPreviousOption(current);
  }

  protected getCurrentStringValue(): string | null {
    if (this.multi() && this.multiModel.length > 0) {
      return this.multiModel.map((opt) => opt.value).join(',');
    } else if (!this.multi() && this.model?.value) {
      return this.model.value + '';
    } else {
      return null;
    }
  }

  protected getMultiValueDefaultString(options: MuiOption<T>[] = []): string {
    return options.map((opt) => opt.label).join(', ');
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private propagateChange = (_: T | T[] | null | undefined): void => {
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private propagateTouch = (): void => {
  };

  private getOptionIndex(element: Element): number {
    return this.optionElements ? this.optionElements.map((ref) => ref.nativeElement).indexOf(element) : -1;
  }
}
